import { useContext, useEffect, useState } from 'react'
import { useNavigate, useParams, useSearchParams } from 'react-router-dom'
import { UserNotificationsContext } from '../../../../context/user-notifications/user-notifications';
import SubsidiaryService from '../../../../shared/services/subsidiary/subsidiary.service';
import { IPostSubsidiary, ISubsidiary } from '../../../../shared/services/subsidiary/subsidiary.model';
import { SubsidiaryForm } from '../../../presentation-components/organisation-components/subsidiary-form';
import { PageOuterContainer } from '../../../../root-components/layouts/page-outer-container';
import { PageHeading } from '../../../presentation-components/display-elements-components/page-heading';
import { ReportPeriodContext } from '../../../../context/report-periods/report-periods';
import { Box, Tab, Tabs } from '@mui/material';
import { a11yProps, deleteQueryParamFromUrl } from '../../../../shared/utils/misc-shared-utils';
import { CustomTabPanel } from '../../../presentation-components/display-elements-components/custom-tab-panel';
import { ClosedReportPeriodMessage } from '../../../presentation-components/miscellaneous-components/closed-report-period-message';
import { StartNewReportPeriodDialog } from '../../../dialogs/settings/report-period/start-new-report-period-dialog';
import { LoadingPage } from '../../loading-page';
import { ScreenLabelsContext } from '../../../../context/screen-labels/screen-labels';
import { Emissions } from '../../../presentation-components/emissons-components/overview/emissions';
import { UserDetailsContext } from '../../../../context/user-details/user-details';
import { IconName } from '../../../../shared/interfaces/icon-picker.model';
import { notifyError } from '../../../../shared/utils/notify-error';

export const OrganisationSubsidiaryEditPage = () => {
  const { id } = useParams();
  const navigate = useNavigate()
  const [searchParams, setSearchParams] = useSearchParams()
  //Context
  const reportPeriodContext = useContext(ReportPeriodContext);
  const userNotificationsContext = useContext(UserNotificationsContext)
  const screenLabelsContext = useContext(ScreenLabelsContext)
  const userDetailsContext = useContext(UserDetailsContext)
  const isAdmin = (userDetailsContext.userDetails?.roles || []).includes('admin')
  const term = screenLabelsContext.getLabel('subsidiary', { plural: false })
  //Services
  const subsidiaryService = SubsidiaryService.getInstance()
  //State
  const [value, setValue] = useState(0)
  const [subsidiary, setSubsidiary] = useState<ISubsidiary>();
  const [errorMessage, setErrorMessage] = useState<string>()
  const [errorCode, setErrorCode] = useState<number>()
  const [editField, setEditField] = useState<string>('')
  const [hasOpenReport, setHasOpenReport] = useState<boolean>(true)
  const [dialogOpen, setDialogOpen] = useState(false);
  //Loading State
  const [isFormSubmitting, setIsFormSubmitting] = useState<boolean>(false)
  const [isStatusSubmitting, setIsStatusSubmitting] = useState<boolean>(false)
  const [isLoading, setIsLoading] = useState<boolean>(true)
  //Use Effects
  useEffect(() => {
    if (searchParams.has('tabId')) {
      const token = searchParams.get('tabId')
      if (token) {
        const tabIndex = parseInt(token)
        setValue(tabIndex)
        if (tabIndex === 1) {
          setIsLoading(false)
        }
      }
      deleteQueryParamFromUrl(searchParams, 'tabId')
    }
  }, [])

  useEffect(() => {
    if (id) {
      if (value === 0) {
        getSubsidiary(id)
      }
    }
  }, [reportPeriodContext.lastSelectedReportId, id, value])

  const getSubsidiary = (id: string) => {
    subsidiaryService.getOne(id)
      .then(response => {
        setSubsidiary(response)
      })
  }

  function refreshSubsidiaries(message: string = 'Updated successfully'): void {
    subsidiaryService.getAll(true)
      .then(() => {
        userNotificationsContext.openSnackbar({ open: true, message });
      })
      .catch(err => {
        console.log(err)
      })
  }

  const submitHandler = (value: IPostSubsidiary) => {
    if (id && value) {
      if (!isFormSubmitting) {
        setErrorMessage('')
        setIsFormSubmitting(true)
        subsidiaryService.update(id, value)
          .then(response => {
            setEditField('')
            getSubsidiary(id)
            setIsFormSubmitting(false)
            refreshSubsidiaries()
          })
          .catch(err => {
            if (err.response.data.message) {
              setErrorMessage(err.response.data.message)
              setErrorCode(err.response.data.code)
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            } else {
              userNotificationsContext.openSnackbar({ open: true, message: 'There was an error, please try again', backgroundColor: 'text.primary' });
            }
            setIsFormSubmitting(false)
            console.log(err)
          })
      }
    }
  }

  // The backend explains every refusal (primary, last active, records attached) in its message, so
  // a 400 is shown as-is rather than mapped to copy here.
  const showRefusal = (err: any, fallbackAction: string) => {
    if (err.response?.status === 400 && err.response?.data?.message) {
      userNotificationsContext.openDialog({
        active: true,
        title: 'Action Not Allowed',
        message: err.response.data.message,
        buttonCopy: 'Ok',
        hideCancelButton: true
      })
    } else {
      notifyError(userNotificationsContext.openSnackbar, err, fallbackAction)
    }
  }

  const deactivateHandler = (subsidiaryId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Deactivating this ${term} stops anyone adding new emissions or records to it. Everything already recorded against it stays in your reports, and you can reactivate it later.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response && !isStatusSubmitting) {
          setIsStatusSubmitting(true)
          subsidiaryService.deactivate(subsidiaryId)
            .then(() => {
              getSubsidiary(subsidiaryId)
              refreshSubsidiaries('Deactivated successfully')
              setIsStatusSubmitting(false)
            })
            .catch(err => {
              showRefusal(err, `deactivating the ${term}`)
              setIsStatusSubmitting(false)
            })
        }
      })
  }

  const reactivateHandler = (subsidiaryId: string) => {
    if (isStatusSubmitting) return
    setIsStatusSubmitting(true)
    subsidiaryService.reactivate(subsidiaryId)
      .then(() => {
        getSubsidiary(subsidiaryId)
        refreshSubsidiaries('Reactivated successfully')
        setIsStatusSubmitting(false)
      })
      .catch(err => {
        showRefusal(err, `reactivating the ${term}`)
        setIsStatusSubmitting(false)
      })
  }

  const deleteHandler = (subsidiaryId: string) => {
    userNotificationsContext.openDialog({
      active: true,
      title: 'Confirmation',
      message: `Are you sure you want to delete this ${term}? This action cannot be undone. A ${term} with records attached cannot be deleted: deactivate it instead.`,
      buttonCopy: 'Continue', cancelButtonCopy: 'Cancel'
    })
      .then(response => {
        if (response && !isStatusSubmitting) {
          setIsStatusSubmitting(true)
          subsidiaryService.remove(subsidiaryId)
            .then(() => {
              setIsStatusSubmitting(false)
              refreshSubsidiaries('Deleted successfully')
              navigate('/organisation/subsidiaries')
            })
            .catch(err => {
              showRefusal(err, `deleting the ${term}`)
              setIsStatusSubmitting(false)
            })
        }
      })
  }

  // Component Event Handlers
  const changeTabHandler = (event: React.SyntheticEvent, newValue: number) => {
    setValue(newValue);
    if (newValue === 0) {
      setIsLoading(true)
    } else {
      setIsLoading(false)
    }
  };

  return (
    <PageOuterContainer>
      {subsidiary &&
        <>
          <PageHeading
            title={subsidiary.name}
            includeBackButton={true}
            backButtonLink='/organisation/subsidiaries'
            backButtonText={`Back to ${screenLabelsContext.getLabel('subsidiary', { plural: true, capitalize: 'first' })}`}
            hasTabs={true}
            showChip={subsidiary.isActive === false}
            chipLabel='Inactive'
            chipColor='default'
            includeActionButton={true}
            actionButtonIcon={subsidiary.isActive === false ? IconName.CheckCircleOutlined : IconName.HideSourceOutlined}
            actionButtonHandler={subsidiary.isActive === false ? () => reactivateHandler(subsidiary.id) : () => deactivateHandler(subsidiary.id)}
            actionButtonText={subsidiary.isActive === false ? 'Reactivate' : 'Deactivate'}
            hideShowActionButton={!isAdmin}
            disableActionButton={isStatusSubmitting}
            includeSecondaryActionButton={true}
            secondaryActionButtonIcon={IconName.DeleteOutline}
            secondaryActionButtonHandler={() => deleteHandler(subsidiary.id)}
            secondaryActionButtonText='Delete'
            hideShowSecondaryActionButton={!isAdmin}
            disableSecondaryActionButton={isStatusSubmitting}
          />
          <Box sx={{ borderBottom: 1, borderColor: 'divider' }}>
            <Tabs value={value} onChange={changeTabHandler}>
              <Tab label='Details'  {...a11yProps(0)} sx={{ borderRadius: 1 }} />
              <Tab label='Emissions' {...a11yProps(1)} sx={{ borderRadius: 1 }} />
            </Tabs>
          </Box>
        </>}
      <CustomTabPanel value={value} index={0}>
        {subsidiary &&
          <SubsidiaryForm
            onSubmit={submitHandler}
            isEditPage={true}
            subsidiary={subsidiary}
            setEditField={setEditField}
            editField={editField}
            errorMessage={errorMessage}
            errorCode={errorCode}
            isSubmitting={isFormSubmitting}
            setErrorMessage={setErrorMessage}
            setErrorCode={setErrorCode}
          />}
      </CustomTabPanel>
      <CustomTabPanel value={value} index={1}>
        {!hasOpenReport &&
          <Box>
            <ClosedReportPeriodMessage openDialog={() => { setDialogOpen(true) }} />
            {dialogOpen && <StartNewReportPeriodDialog open={dialogOpen} closeHandler={() => { setDialogOpen(false) }} />}
          </Box>}
        {hasOpenReport &&
          <LoadingPage pageReady={!isLoading}>
            {reportPeriodContext.lastSelectedReportId && id && (
              <Emissions
                includeDelete={false}
                includeEdit={false}
                queryString={`&subsidiaryId=${id}`}
                backLink={`/organisation/subsidiaries/${id}?tabId=1`}
                backText='Back to subsidiary'
              />
            )}
          </LoadingPage>}
      </CustomTabPanel>
    </PageOuterContainer>
  )
}
